import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

export interface HistEntry {
  ts: number;
  tipo: string;
  texto: string;
}

export interface Client {
  client_id: string;
  nombre: string;
  email?: string;
  telefono?: string;
  profile_types: string[];
  notas?: string;
  origen?: string;
  historial?: HistEntry[];
  updated_at?: number;
  /** Tiene cuenta en el portal. Lo escribe PostConfirmation al registrarse. */
  user_id?: string;
  /** Inquilino cuyo email no cuadró con ningún contrato y pide que le vinculen
   *  su vivienda a mano. */
  vinculacion_solicitada?: {
    direccion: string;
    mensaje?: string;
    estado: string;
    created_at: number;
  };
}

export interface Lead {
  lead_id: string;
  tipo: string;
  servicio?: string;
  servicio_nombre?: string;
  /** La web permite pedir varios servicios en una sola solicitud. */
  servicios?: string[];
  servicios_nombres?: string[];
  nombre: string;
  telefono?: string;
  email?: string;
  fecha?: string;
  hora?: string;
  mensaje?: string;
  interes?: string;
  origen?: string;
  estado: 'nuevo' | 'contactado' | 'cerrado' | 'convertido';
  email_estado?: string;
  client_id?: string;
  created_at: string;
  /** Solo en leads de suministros (tipo 'servicio' desde el portal): el estudio
   *  de la factura que originó la solicitud viaja resumido en el propio lead. */
  bill_id?: string;
  ahorro_anual?: number;
  comercializadora?: string;
  /** Solo en leads de la calculadora de hipoteca: la simulación que hizo el
   *  visitante, para no tener que preguntarle lo que ya calculó. `cuota_estimada`
   *  puede faltar — si el ahorro no daba para la operación, no hubo cuota. */
  precio_vivienda?: number;
  entrada?: number;
  plazo_anios?: number;
  cuota_estimada?: number;
  /** Puesto al registrar la comisión de socio: marca el lead como ya tramitado. */
  commission_id?: string;
}

export interface Estudio {
  bill_id: string;
  suministro?: string;
  estudio: Record<string, number | string | boolean | unknown>;
  datos: Record<string, number | string>;
}

export function useClients() {
  return useQuery<Client[]>({
    queryKey: ['clients'],
    queryFn: async () => (await api.get('/crm/clients')).data.items,
  });
}

export function useClient(id: string | null) {
  return useQuery<Client>({
    queryKey: ['clients', id],
    queryFn: async () => (await api.get(`/crm/clients/${id}`)).data,
    enabled: Boolean(id),
  });
}

export function useSaveClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (c: Partial<Client> & { client_id?: string }) => {
      const { client_id, ...body } = c;
      const res = client_id
        ? await api.put(`/crm/clients/${client_id}`, body)
        : await api.post('/crm/clients', body);
      return res.data as Client;
    },
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.setQueryData(['clients', saved.client_id], saved);
    },
  });
}

export function useAddNota(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (texto: string) =>
      api.post(`/crm/clients/${clientId}/historial`, { texto }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients', clientId] }),
  });
}

export function useLeads() {
  return useQuery<Lead[]>({
    queryKey: ['leads'],
    queryFn: async () => (await api.get('/crm/leads')).data.items,
    refetchInterval: 60_000,
  });
}

export function useSetLeadEstado() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ leadId, estado }: { leadId: string; estado: string }) =>
      api.patch(`/crm/leads/${leadId}`, { estado }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads'] }),
  });
}

/** El estudio de la factura que originó un lead de suministros. Bajo demanda:
 *  solo se pide cuando se abre «ver estudio». */
export function useLeadEstudio(leadId: string | null) {
  return useQuery<Estudio>({
    queryKey: ['lead-estudio', leadId],
    queryFn: async () => (await api.get(`/crm/leads/${leadId}/estudio`)).data,
    enabled: Boolean(leadId),
  });
}

/** Registra la comisión de socio de un lead de servicio (2º origen). Toca leads
 *  (queda cerrado) y comisiones (que leerá C5). */
export function useRegistrarComisionServicio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ leadId, importe }: { leadId: string; importe: number }) =>
      (await api.post(`/crm/leads/${leadId}/comision`, { importe })).data as {
        commission_id: string;
      },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads'] });
      qc.invalidateQueries({ queryKey: ['commissions'] });
    },
  });
}

export function useConvertLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (leadId: string) =>
      (await api.post(`/crm/leads/${leadId}/convert`)).data as { client_id: string },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads'] });
      qc.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

export function useImportRows() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { tipo: 'clientes' | 'viviendas'; rows: Record<string, unknown>[] }) =>
      (await api.post('/crm/import', payload)).data as {
        creados: number;
        errores: { fila: number; error: string }[];
      },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['properties'] });
    },
  });
}
